import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Text, Icon, LoaderPulse } from '@reservamos/elements';
import iosWallet from 'images/purchases/wallet-ios.svg';
import googleWallet from 'images/purchases/wallet-google.svg';

/**
 * WalletButton component for Apple and Google Wallet integration
 * @param {Object} props - onClick, isGoogle, disabled, isLoading.
 * @param {Function} props.onClick - Click handler function
 * @param {boolean} props.isGoogle - Whether to show Google Wallet instead of Apple Wallet
 * @param {boolean} props.disabled - Whether the button is disabled
 * @param {boolean} props.isLoading - Whether the button is in loading state
 */
const WalletButton = ({ onClick, isGoogle, disabled, isLoading }) => {
  const { t } = useTranslation('purchase');

  const handleClick = (e) => {
    if (disabled || isLoading) {
      return;
    }
    onClick(e);
  };

  return (
    <button
      type="button"
      className={`bg-[#000000] flex justify-center items-center py-1.5 ${
        isGoogle ? 'rounded-full' : 'rounded-xl'
      } w-full lg:hidden`}
      onClick={handleClick}
      disabled={disabled || isLoading}
      aria-label="Add to wallet"
      aria-busy={isLoading}
    >
      {isLoading ? (
        <LoaderPulse size="S" color="white" hasWrapper={false} />
      ) : (
        <div className="flex gap-2 items-center">
          <Icon type={isGoogle ? googleWallet : iosWallet} />
          <div>
            <Text color="white" size="XXS" lineHeight="normal">
              {isGoogle
                ? t('ticket_wallet_download.wallet_add_to_google_prefix')
                : t('ticket_wallet_download.wallet_add_to_apple_prefix')}
            </Text>
            <Text color="white" size="XS" lineHeight="normal">
              {isGoogle
                ? t('ticket_wallet_download.wallet_google_wallet')
                : t('ticket_wallet_download.wallet_apple_wallet')}
            </Text>
          </div>
        </div>
      )}
    </button>
  );
};

WalletButton.propTypes = {
  onClick: PropTypes.func,
  isGoogle: PropTypes.bool,
  disabled: PropTypes.bool,
  isLoading: PropTypes.bool,
};

WalletButton.defaultProps = {
  onClick: () => {},
  isGoogle: false,
  disabled: false,
  isLoading: false,
};

export default WalletButton;
